import { useRef, useState } from "react";

import type { ComplianceTrendRow } from "../api/types";
import { complianceTone } from "../format";

/**
 * Charts, drawn as plain SVG.
 *
 * No charting library. The two shapes this panel needs are a line over months and a stack
 * of three counts, and a dependency that renders them would bring a canvas renderer, its
 * own theming layer and a version to keep current — for marks that are twenty lines of
 * path arithmetic. The palette lives in `styles.css` with the rest of the design tokens
 * rather than in a JavaScript theme object, so a chart cannot drift from the UI around it.
 *
 * Every chart here is paired with the table of the same figures on the same screen. The
 * chart shows the shape; the table remains the thing you read a number off, which is also
 * what makes the colours safe — no value is ever carried by hue alone.
 */

/** A month on the x axis. ISO throughout the panel, so `2026-08` rather than `Aug`. */
function monthLabel(periodMonth: string): string {
  return periodMonth.slice(0, 7);
}

interface Geometry {
  width: number;
  height: number;
  padTop: number;
  padRight: number;
  padBottom: number;
  padLeft: number;
}

const GEOMETRY: Geometry = {
  width: 760,
  height: 220,
  padTop: 14,
  padRight: 18,
  padBottom: 30,
  padLeft: 42,
};

const plotWidth = GEOMETRY.width - GEOMETRY.padLeft - GEOMETRY.padRight;
const plotHeight = GEOMETRY.height - GEOMETRY.padTop - GEOMETRY.padBottom;

/**
 * Which band the pointer is over.
 *
 * Measured from the rendered width rather than the viewBox, because the SVG scales to its
 * card — reading `offsetX` against the 760-unit coordinate space would put the tooltip
 * progressively further from the cursor the narrower the window got.
 */
function useHoverIndex(count: number): {
  index: number | null;
  handlers: {
    onMouseMove: (event: React.MouseEvent<SVGSVGElement>) => void;
    onMouseLeave: () => void;
  };
  ref: React.RefObject<SVGSVGElement>;
} {
  const ref = useRef<SVGSVGElement>(null);
  const [index, setIndex] = useState<number | null>(null);

  return {
    index,
    ref,
    handlers: {
      onMouseMove: (event) => {
        const svg = ref.current;
        if (svg === null || count === 0) return;

        const box = svg.getBoundingClientRect();
        const scale = GEOMETRY.width / box.width;
        const x = (event.clientX - box.left) * scale - GEOMETRY.padLeft;
        const band = plotWidth / count;
        const found = Math.floor(x / band);

        setIndex(found < 0 || found >= count ? null : found);
      },
      onMouseLeave: () => {
        setIndex(null);
      },
    },
  };
}

/**
 * A round top for a count axis, and the ticks below it.
 *
 * Scaling straight to the tallest bar puts the axis maximum at whatever that month happened
 * to be — an axis topping out at 7 is read as a threshold rather than as an accident of the
 * data. Rounded up to 1/2/5 × a power of ten, with whole-number ticks only, because a
 * submission count of 2.5 does not exist.
 */
function countTicks(tallest: number): { max: number; ticks: number[] } {
  const magnitude = 10 ** Math.floor(Math.log10(Math.max(tallest, 1)));
  const step =
    [1, 2, 5, 10].find((factor) => factor * magnitude >= tallest) ?? 10;
  const max = step * magnitude;

  // Prefer four or five gaps, but only where every tick lands on a whole submission.
  const divisions = [5, 4, 3, 2].find((count) => max % count === 0) ?? 1;
  const ticks = Array.from(
    { length: divisions + 1 },
    (_, index) => (max / divisions) * index,
  );

  return { max, ticks };
}

/** Horizontal gridlines at 0/25/50/75/100, kept recessive so the data reads first. */
function PercentGrid(): JSX.Element {
  return (
    <g aria-hidden="true">
      {[0, 25, 50, 75, 100].map((value) => {
        const y = GEOMETRY.padTop + plotHeight - (value / 100) * plotHeight;
        return (
          <g key={value}>
            <line
              x1={GEOMETRY.padLeft}
              x2={GEOMETRY.width - GEOMETRY.padRight}
              y1={y}
              y2={y}
              stroke="var(--chart-grid)"
              strokeWidth={1}
            />
            <text
              x={GEOMETRY.padLeft - 8}
              y={y + 4}
              textAnchor="end"
              className="chart-tick"
            >
              {value}%
            </text>
          </g>
        );
      })}
    </g>
  );
}

/**
 * Compliance over time.
 *
 * One series, so there is no legend — the card title names it. The point markers are drawn
 * at every month rather than only on hover: with a handful of months the line alone leaves
 * it ambiguous whether a bend is a data point or a rendering artefact.
 */
export function ComplianceTrendChart({
  rows,
}: {
  rows: readonly ComplianceTrendRow[];
}): JSX.Element {
  const points = rows.filter((row) => row.compliancePercent !== null);
  const { index, handlers, ref } = useHoverIndex(points.length);

  if (points.length === 0) {
    return <p className="empty">Not enough history to draw a trend yet.</p>;
  }

  const band = plotWidth / points.length;
  const xOf = (position: number): number =>
    GEOMETRY.padLeft + band * position + band / 2;
  const yOf = (percent: number): number =>
    GEOMETRY.padTop +
    plotHeight -
    (Math.max(0, Math.min(100, percent)) / 100) * plotHeight;

  const line = points
    .map(
      (row, position) =>
        `${position === 0 ? "M" : "L"} ${xOf(position)} ${yOf(row.compliancePercent!)}`,
    )
    .join(" ");

  // Closed back along the baseline so the area sits under the line.
  const area =
    `${line} L ${xOf(points.length - 1)} ${GEOMETRY.padTop + plotHeight}` +
    ` L ${xOf(0)} ${GEOMETRY.padTop + plotHeight} Z`;

  const active = index === null ? null : points[index];

  return (
    <div className="chart">
      <div className="chart-scroll">
        <svg
          ref={ref}
          viewBox={`0 0 ${GEOMETRY.width} ${GEOMETRY.height}`}
          className="chart-svg"
          role="img"
          aria-label="Compliance percentage by month"
          {...handlers}
        >
          <PercentGrid />

          {points.length > 1 && (
            <path d={area} fill="var(--chart-line)" opacity={0.1} />
          )}
          {points.length > 1 && (
            <path
              d={line}
              fill="none"
              stroke="var(--chart-line)"
              strokeWidth={2}
              strokeLinejoin="round"
            />
          )}

          {points.map((row, position) => (
            <g key={row.periodMonth}>
              <circle
                cx={xOf(position)}
                cy={yOf(row.compliancePercent!)}
                r={index === position ? 6 : 4}
                fill="var(--surface)"
                stroke="var(--chart-line)"
                strokeWidth={2}
              />
              <text
                x={xOf(position)}
                y={GEOMETRY.height - 10}
                textAnchor="middle"
                className="chart-tick"
              >
                {monthLabel(row.periodMonth)}
              </text>
            </g>
          ))}

          {index !== null && (
            <line
              x1={xOf(index)}
              x2={xOf(index)}
              y1={GEOMETRY.padTop}
              y2={GEOMETRY.padTop + plotHeight}
              stroke="var(--text-muted)"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
          )}
        </svg>
      </div>

      {/* Always rendered, so the card does not change height as the pointer enters. */}
      <p className="chart-readout">
        {active === undefined || active === null ? (
          <span className="chart-readout-rest">
            Hover a month for its figures.
          </span>
        ) : (
          <>
            <strong>{monthLabel(active.periodMonth)}</strong> ·{" "}
            {active.compliancePercent}% compliant · {active.acceptedCount} of{" "}
            {active.totalSubmissions} accepted
          </>
        )}
      </p>
    </div>
  );
}

/**
 * How each month's submissions were decided.
 *
 * Stacked counts rather than percentages: a month with four visits and a month with four
 * hundred should not draw the same bar. Segments carry a two-pixel gap so the boundary
 * survives when the colours do not — the same reason the legend is always present.
 */
export function VerdictMixChart({
  rows,
}: {
  rows: readonly ComplianceTrendRow[];
}): JSX.Element {
  const { index, handlers, ref } = useHoverIndex(rows.length);

  if (rows.length === 0) {
    return <p className="empty">No submissions in this period.</p>;
  }

  const tallest = Math.max(...rows.map((row) => row.totalSubmissions), 1);
  const { max: axisMax, ticks } = countTicks(tallest);
  const band = plotWidth / rows.length;
  const barWidth = Math.min(46, band * 0.6);

  const segmentsOf = (row: ComplianceTrendRow) =>
    [
      {
        key: "Accepted",
        count: row.acceptedCount,
        fill: "var(--chart-accepted)",
      },
      { key: "Flagged", count: row.flaggedCount, fill: "var(--chart-flagged)" },
      { key: "Blocked", count: row.blockedCount, fill: "var(--chart-blocked)" },
    ].filter((segment) => segment.count > 0);

  const active = index === null ? null : rows[index];

  return (
    <div className="chart">
      <ul className="chart-legend">
        <li>
          <span
            className="chart-swatch"
            style={{ background: "var(--chart-accepted)" }}
          />{" "}
          Accepted
        </li>
        <li>
          <span
            className="chart-swatch"
            style={{ background: "var(--chart-flagged)" }}
          />{" "}
          Flagged
        </li>
        <li>
          <span
            className="chart-swatch"
            style={{ background: "var(--chart-blocked)" }}
          />{" "}
          Blocked
        </li>
      </ul>

      <div className="chart-scroll">
        <svg
          ref={ref}
          viewBox={`0 0 ${GEOMETRY.width} ${GEOMETRY.height}`}
          className="chart-svg"
          role="img"
          aria-label="Submissions by verdict, by month"
          {...handlers}
        >
          <g aria-hidden="true">
            {ticks.map((value) => {
              const y =
                GEOMETRY.padTop + plotHeight - (value / axisMax) * plotHeight;
              return (
                <g key={value}>
                  <line
                    x1={GEOMETRY.padLeft}
                    x2={GEOMETRY.width - GEOMETRY.padRight}
                    y1={y}
                    y2={y}
                    stroke={value === 0 ? "var(--border)" : "var(--chart-grid)"}
                    strokeWidth={1}
                  />
                  <text
                    x={GEOMETRY.padLeft - 8}
                    y={y + 4}
                    textAnchor="end"
                    className="chart-tick"
                  >
                    {value}
                  </text>
                </g>
              );
            })}
          </g>

          {rows.map((row, position) => {
            const centre = GEOMETRY.padLeft + band * position + band / 2;
            let cursor = GEOMETRY.padTop + plotHeight;

            return (
              <g
                key={row.periodMonth}
                opacity={index === null || index === position ? 1 : 0.55}
              >
                {segmentsOf(row).map((segment) => {
                  const height = (segment.count / axisMax) * plotHeight;
                  // Two pixels of surface between segments, taken off the top of each.
                  const drawn = Math.max(height - 2, 1);
                  cursor -= height;
                  return (
                    <rect
                      key={segment.key}
                      x={centre - barWidth / 2}
                      y={cursor}
                      width={barWidth}
                      height={drawn}
                      rx={3}
                      fill={segment.fill}
                    />
                  );
                })}
                <text
                  x={centre}
                  y={GEOMETRY.height - 10}
                  textAnchor="middle"
                  className="chart-tick"
                >
                  {monthLabel(row.periodMonth)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <p className="chart-readout">
        {active === undefined || active === null ? (
          <span className="chart-readout-rest">
            Hover a month for its figures.
          </span>
        ) : (
          <>
            <strong>{monthLabel(active.periodMonth)}</strong> ·{" "}
            {active.totalSubmissions} submitted · {active.acceptedCount}{" "}
            accepted · {active.flaggedCount} flagged · {active.blockedCount}{" "}
            blocked
          </>
        )}
      </p>
    </div>
  );
}

/**
 * A sparkline for a stat tile.
 *
 * No axes, no grid, no labels — the tile's own value says where the series ends, and the
 * line only has to say how it got there. Drawn in a de-emphasised hue with the most recent
 * point picked out, so a tile scanned at a glance still shows which end is now.
 *
 * Returns nothing below two points. A single point drawn as a flat line is a claim about a
 * trend that one month of data cannot support.
 */
export function Sparkline({ values }: { values: readonly number[] }): JSX.Element | null {
  const recent = values.slice(-12);
  if (recent.length < 2) return null;

  const width = 96;
  const height = 26;
  const pad = 3;

  const highest = Math.max(...recent);
  const lowest = Math.min(...recent);
  const span = highest - lowest;

  const xOf = (index: number): number => pad + (index / (recent.length - 1)) * (width - pad * 2);
  // A flat series sits on the midline rather than dividing by a zero span.
  const yOf = (value: number): number =>
    span === 0 ? height / 2 : height - pad - ((value - lowest) / span) * (height - pad * 2);

  const path = recent.map((value, index) => `${index === 0 ? 'M' : 'L'} ${xOf(index)} ${yOf(value)}`).join(' ');
  const lastIndex = recent.length - 1;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="sparkline"
      role="img"
      aria-label={`Trend over the last ${recent.length} months`}
    >
      <path d={path} fill="none" stroke="var(--sparkline-line)" strokeWidth={1.5} strokeLinejoin="round" />
      <circle cx={xOf(lastIndex)} cy={yOf(recent[lastIndex]!)} r={2.5} fill="var(--chart-line)" />
    </svg>
  );
}

export interface RankedItem {
  key: string;
  label: string;
  sublabel?: string;
  value: number | null;
  count: number;
}

/**
 * A ranked comparison, worst first.
 *
 * The job here is "which of these is worst", which is magnitude comparison across named
 * categories — a bar chart, not a table. The table stays below it: the chart is for finding
 * the outlier, the table for reading the numbers that explain it.
 *
 * Built from HTML rather than SVG on purpose. Text inside a viewBox scales with the
 * viewBox, which had already collapsed axis labels to a few pixels on a narrow screen once
 * in this panel. Bars are divs, so the labels are ordinary text at ordinary sizes and the
 * whole thing reflows instead of shrinking.
 *
 * Colour repeats the compliance bands the tables use rather than a single hue: the
 * thresholds are the panel's existing standard, and a chart that ranked by length while
 * ignoring them would invite reading 60% and 89% as the same kind of result.
 */
export function RankedBars({
  items,
  countLabel,
  emptyText,
}: {
  items: readonly RankedItem[];
  countLabel: string;
  emptyText: string;
}): JSX.Element {
  if (items.length === 0) return <p className="empty">{emptyText}</p>;

  return (
    <ul className="ranked">
      {items.map((item) => {
        const measured = item.value !== null;
        const width = measured ? Math.max(0, Math.min(100, item.value!)) : 0;

        return (
          <li className="ranked-row" key={item.key}>
            <span className="ranked-head">
              <span className="ranked-label" title={item.label}>
                {item.label}
              </span>
              {item.sublabel !== undefined && <span className="ranked-sub">{item.sublabel}</span>}
            </span>

            <span className="ranked-track">
              <span
                className={`ranked-fill ranked-${measured ? complianceTone(width) : 'none'}`}
                style={{ width: `${measured ? Math.max(width, 1.5) : 0}%` }}
              />
            </span>

            <span className="ranked-figures">
              {/* Tabular here, unlike a stat tile: these are a column the eye reads down. */}
              <span className={`ranked-value${measured ? '' : ' muted'}`}>
                {measured ? `${Math.round(width)}%` : '—'}
              </span>
              <span className="ranked-count">
                {item.count} {countLabel}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
